/**
 * MachineScheduleIndexTestRows.tsx
 * Gigi/Mike feedback 2026-07-31 ("we only do the index… it is by test — we
 * didn't assign the machine… there's only one index for moisture"): the
 * Machine Schedule's INDEX category no longer lists the physical stations.
 * Instead it renders ONE ROW PER INDEX TEST (the full active IsIndexTest
 * roster — idle tests show as 0% lanes), each cell = that test's own daily
 * utilization % of its own tests/day capacity (per-type model, v1.1.68).
 *
 * Lab-wide (BASELINE) perspective — `getIndexCapacity(from, to)` without a
 * scenarioKey, same rule as the rest of the page. Read-only: index tests are
 * planned via quotes/scenario plans (Phase 2), never machine bookings (A4).
 * Supersedes MachineScheduleIndexRow (the single pooled lane).
 */

import React, { useEffect, useState } from 'react';
import { Box, CircularProgress, Tooltip, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';

import {
  getIndexCapacity,
  type IndexCapacityTypeInfo,
  type IndexDayCapacity,
} from '../../../services/scheduleIndex';
import { IndexUtilizationCell } from '../../../components/schedule/IndexUtilizationCell';
import type { IndexRowTooltipDay } from '../../../utils/indexUtilizationTooltip';
import { isToday as isTodayIso } from './scheduleGridShared';

export interface MachineScheduleIndexTestRowsProps {
  /** ISO dates ('YYYY-MM-DD') for the visible window, in order — matches the grid's day columns. */
  days: string[];
  /** Day column width in px (density / zoom) — matches the sibling rows exactly. */
  dayColWidth: number;
  /** Row height in px. */
  rowHeight: number;
  /** Sticky label column width — matches the grid's LEFT_COL_WIDTH. */
  labelWidth: number;
  /** Compact label mode (matches the grid rows). */
  compact?: boolean;
}

function errorMessage(err: unknown, fallback: string): string {
  const apiMsg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
  if (apiMsg) return apiMsg;
  return err instanceof Error ? err.message : fallback;
}

/** Synthesize the per-type day object the shared % cell renders. */
function typeDay(date: string, day: IndexDayCapacity | undefined, taskTypeKey: string): IndexRowTooltipDay {
  const entry = day?.byType.find((b) => b.taskTypeKey === taskTypeKey) ?? null;
  return {
    date,
    isWorkingDay: day?.isWorkingDay ?? true,
    firmPct: entry?.firmPct ?? 0,
    softPct: entry?.softPct ?? 0,
    maxTypeFirmPct: entry?.firmPct ?? 0,
    maxTypeSoftPct: entry?.softPct ?? 0,
    isOverCapacity: entry?.isOverCapacity ?? false,
    hasUnknownCapacity: entry?.hasUnknownCapacity ?? false,
    byType: entry ? [entry] : [],
  };
}

export const MachineScheduleIndexTestRows: React.FC<MachineScheduleIndexTestRowsProps> = ({
  days,
  dayColWidth,
  rowHeight,
  labelWidth,
  compact = false,
}) => {
  const theme = useTheme();
  const [types, setTypes] = useState<IndexCapacityTypeInfo[]>([]);
  const [dayByDate, setDayByDate] = useState<Map<string, IndexDayCapacity>>(() => new Map());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const start = days[0];
  const end = days[days.length - 1];

  useEffect(() => {
    if (!start || !end) {
      setTypes([]);
      setDayByDate(new Map());
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    // No scenarioKey — lab-wide BASELINE perspective (I13).
    getIndexCapacity(start, end)
      .then((res) => {
        if (cancelled) return;
        setTypes(res.types ?? []);
        setDayByDate(new Map(res.days.map((d) => [d.date, d])));
      })
      .catch((err) => {
        if (!cancelled) setError(errorMessage(err, 'Failed to load index test utilization.'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [start, end]);

  const borderColor = theme.border.color;

  if (error) {
    return (
      <Tooltip title={error} arrow placement="top">
        <Box sx={{ px: 2, py: 1, backgroundColor: alpha(theme.schedule.heatmapHigh, 0.06) }}>
          <Typography variant="caption" color="error" sx={{ fontSize: '0.68rem' }}>
            Index test utilization unavailable
          </Typography>
        </Box>
      </Tooltip>
    );
  }

  if (loading && types.length === 0) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1 }}>
        <CircularProgress size={14} thickness={5} />
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem' }}>
          Loading index tests…
        </Typography>
      </Box>
    );
  }

  return (
    <>
      {types.map((t) => (
        <Box
          key={t.taskTypeKey}
          data-index-test-key={t.taskTypeKey}
          sx={{
            display: 'flex',
            height: rowHeight,
            borderBottom: `1px solid ${alpha(borderColor, 0.5)}`,
            minWidth: labelWidth + days.length * dayColWidth,
            '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.02) },
          }}
        >
          {/* Sticky test label — mirrors MachineRowLabelCell's geometry. */}
          <Box
            title="Index tests are planned via quotes/scenario plans (Phase 2 tab), by test — never booked on a machine."
            sx={{
              width: labelWidth,
              minWidth: labelWidth,
              px: 1.5,
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              borderRight: `1px solid ${borderColor}`,
              position: 'sticky',
              left: 0,
              zIndex: 2,
              backgroundColor: theme.palette.background.paper,
            }}
          >
            <Typography
              noWrap
              sx={{ fontSize: compact ? '0.68rem' : '0.74rem', fontWeight: 600 }}
            >
              {t.taskTypeName}
            </Typography>
            {!compact && (
              <Typography
                variant="caption"
                noWrap
                sx={{ ml: 'auto', fontSize: '0.6rem', color: 'text.secondary' }}
              >
                {t.capacity != null ? `${t.capacity}/day` : ''}
              </Typography>
            )}
          </Box>

          {/* Per-day % cells — this test's own share of its own capacity. */}
          <Box sx={{ width: days.length * dayColWidth, flexShrink: 0, display: 'flex' }}>
            {days.map((date) => (
              <IndexUtilizationCell
                key={date}
                width={dayColWidth}
                height={rowHeight}
                isToday={isTodayIso(date)}
                day={typeDay(date, dayByDate.get(date), t.taskTypeKey)}
              />
            ))}
          </Box>
        </Box>
      ))}
    </>
  );
};
